/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2014, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./Filters',
	'underscore'
], function(
	Class,
	Filters,
	_
) {
	'use strict';

	// Used to identify PageContextEntry origin. Origin type 'filter' are docker filters 
	// while origin type 'visualization' are selection filters triggered by selection (click) event in a visualization.
	const FILTER = 'filter';
	const VISUALIZATION = 'visualization';

	// Values in filter spec are either of type 'use' by default (uniqueNames) or 'display' (labels) under some conditions.
	const DISPLAY = 'display';

	/**
	 * INTENT: VisFilterProxy manages all of the different ways filters can be applied to a visualization.
	 * Filters can manifest themselves as dataItemSelections, pageContext or filter collections.
	 * This object should be set up with its inputs and outputs and the proxyAPI's should be called to hide
	 * the implementation details.
	 */
	const VisFilterProxy = Class.extend({

		/**
		 * Used for mocking
		 */
		// constructor

		/**
		 * @params params - expect an input, output and, optionally, a dataPointOutput to be specified.
		 */
		init: function(params) {
			VisFilterProxy.inherited('init', this, arguments);

			this.logger = params.logger;

			_.forEach(params, function(param, key) {
				this[key] = param;
			}.bind(this));

			this.dataItemAPI = null; //Temporarily disable dataItemAPI updates.

			//Determine the input source.
			this.input = this.setInput(params.input);
			this.output = this.setOutput(params.output);

			//By Default, datapoint output is the same as output but can be overridden.
			//Example - For cases such as converting a mix of edge and point selections to local filters, they would be set
			//to output edge selections to dataItems and point selections to filters.
			this.dataPointOutput = ((params.dataPointOutput instanceof Filters) && this._getFiltersOutput(params.output)) ||
				// livewidget-cleanup [todo] we need to recover the logic below...
				/*(params.dataPointOutput instanceof DataItemAPI) && this._getDataItemOutput(params.output) || */
				(params.dataPointOutput && this._getPageContextOutput(params.output)) ||
				this.output;

			if (!this.input || !this.output) {
				this.logger.error('Attempt to use VisFilterProxy without both an input and output specified.');
			}
		},

		setInput: function(input) {
			this.input = ((input instanceof Filters) && this._getFiltersInput(input)) ||
				// livewidget-cleanup [todo] we need to recover the logic below...
				/* ((input instanceof DataItemAPI) && this._getDataItemInput(input)) || */
				(this._getPageContextInput(input));
			return this.input;
		},

		setOutput: function(output) {
			this.output = ((output instanceof Filters) && this._getFiltersOutput(output)) ||
				// livewidget-cleanup [todo] we need to recover the logic below...
				/* ((output instanceof DataItemAPI) && this._getDataItemOutput(output)) || */
				(output && this._getPageContextOutput(output));
			return this.output;
		},

		/**
		 * @param itemContext - the itemContext of the item to return values for (at minimum, it must contain an itemId)
		 * @returns the values for the filter specified by the itemContext as an array of .u and .d
		 */
		getFilterValues: function(itemContext) {
			return this.input.getFilterValues(itemContext);
		},

		getExclude: function(itemContext) {
			return this.input.getExclude(itemContext);
		},

		getConditionInfo: function(itemContext) {
			return this.input.getConditionsInfo(itemContext);
		},

		getOperator: function(itemContext) {
			return this.input.getFilterOperator(itemContext);
		},

		/**
		 * @param itemContext - the itemContext of the item to return values for (at minimum, it must contain an itemId)
		 * @param updateSpec - a filter update specification that includes values, exclude, conditionsInfo, action (like update, replace, keepexclude) etc.
		 */
		updateFilter: function(itemContext, updateSpec, options) {
			if (this.action) {
				updateSpec.action = updateSpec.action || this.action; //Override the default action if its set on the VisFilterProxy.
			}
			return this.output.updateFilter(itemContext, updateSpec, /*type*/ null, options);
		},

		updateRangeFilter: function(itemContext, updateSpec, options) {
			return this.output.updateRangeFilter(itemContext, updateSpec, options);
		},

		removeFilterValues: function(itemContext, options) {
			return this.output.removeFilterValues(itemContext, options);
		},

		addDataPoints: function(itemContext, updateSpec, options) {
			return this.output.addDataPoints(itemContext, updateSpec, options);
		},

		/**
		 * construct a map of all visible items based on the passed in slotAPIs
		 * @returns a map of slotAPIs keyed on the itemId of the dataItem.
		 */
		_buildVisibleItemsMap: function(slotAPIs) {
			var visibleItemsMap = {};
			slotAPIs = slotAPIs || [];
			_.each(slotAPIs, function(slotAPI) {
				// be compatible with new SlotAPI
				if (slotAPI.getInterfaceType && slotAPI.getImplType) {
					_.each(slotAPI.getDataItemList(), function(dataItem) {
						if (dataItem.getType() !== 'fact') {
							// We should filter on the 'fact' dataItem instead of highlight
							// APAR: PI90584
							visibleItemsMap[dataItem.getColumnId()] = true;
						}
					});
				}
			});
			return visibleItemsMap;
		},

		/**
		 * construct the set of items that are in a dataPoint filter.
		 * if the item is not a filter (ie: its a visualization entry that is visible),
		 * an empty list is returned - it is not a dataPointFilter.
		 * @returns the list of items for a valid dataPoint filter (or an empty list if it is not a dataPointFilter)
		 */
		_buildDataPointFilterItems: function(pageContextItem, visibleItemsMap) {
			const dataItemIds = pageContextItem.getItemIds();
			const isVisible = _.every(dataItemIds, dataItemId => visibleItemsMap[dataItemId]);
			return isVisible && pageContextItem.getOrigin() === VISUALIZATION ? [] : dataItemIds;
		},

		/**
		 * Convert all filters in the input into the output
		 * (note: Currently, only pageContext is supported as input by this function)
		 * @param slotAPIs (optional) - if included, only items which are not in the slotAPIs will be converted to filters.
		 * @param options (optional) - if included, options will be honoured.
		 * @returns true if the filters have been modified.
		 */
		convertFilters: function(slotAPIs, options = {}) {
			let pageContextItems = this._getInputPageContextItems();
			var visibleItemsMap = (slotAPIs) ? this._buildVisibleItemsMap(slotAPIs) : {};
			var modified = false;
			for(let i=0; i< pageContextItems.length; i++) {
				const pageContextItem = pageContextItems[i];
				var itemContext = {
					origin: pageContextItem.getOrigin(),
					sourceId: pageContextItem.getSourceId(),
					scope: pageContextItem.getScope(),
					eventGroupId: pageContextItem.getEventGroupId(),
					itemName: pageContextItem.getItemName(),
					eventSourceId: pageContextItem.getEventSourceId(),
					visualizationId: pageContextItem.getOrigin() === VISUALIZATION && pageContextItem.getEventSourceId()
				};
				if(this._shouldTurnOffBrushing(itemContext, options.content)){
					visibleItemsMap = {};//turn off brushing, always do fitlering
				}
				let valueType = pageContextItem.getIsSynchronizedEntry() ? DISPLAY : pageContextItem.getValueType();
				if (pageContextItem.isDataPointType()) {
					const dataPointItems = this._buildDataPointFilterItems(pageContextItem, visibleItemsMap);
					if (dataPointItems.length) {
						itemContext.itemIds = dataPointItems;
						itemContext.binningAPIs = pageContextItem.getBinningAPIs();
						this.dataPointOutput.addDataPoints(
							itemContext,
							//updateSpec:
							{
								values: pageContextItem.getDataPoints(),
								exclude: (pageContextItem.getExclude() || this.exclude),
								action: this.action || 'update',
								type: valueType
							},
							options);
						modified = true;
					}
				} else if (pageContextItem.getOrigin() === FILTER || !visibleItemsMap[pageContextItem.getItemId()]) {
					modified = true;
					itemContext.itemId = pageContextItem.getItemId();
					itemContext.binning = pageContextItem.getBinningAPI();
					itemContext.binningAPIs = pageContextItem.getBinningAPIs();
					if (!valueType) {
						const visualization = options.content && options.content.getFeature('Visualization');
						const dataSource = visualization && visualization.getDataSource();
						const metadataColumn = dataSource && dataSource.getMetadataColumn(itemContext.itemId);
						valueType = metadataColumn && metadataColumn.isProperty() ? DISPLAY : null;
					}
					this._convertEdgeFilter(itemContext, pageContextItem, valueType, options);
				}
			}
			return modified;
		},
		/*
		 * Turn off brushing and always do filtering if an event receiving widget
		 * contains visualization that has turnOffBrushing property turned on (true)
		 *
		 * @param {itemContext} pageContext item
		 * @param {content}  content API
		 * 	}
		 */
		_shouldTurnOffBrushing(itemContext, content){
			return content && content.getId() !== itemContext.eventSourceId && content.getPropertyValue('turnOffBrushing') === true;
		},

		_getInputPageContextItems: function() {
			let pageContextItems;
			if (this.pageContextItems) {
				pageContextItems = this.pageContextItems;
			} else {
				let msg = (!this.selectors) ? 'selectors' : ((!this.input) ? 'input' : null);
				if (msg) {
					this.logger.error(msg + ' must be specified on the constructor to use the convertFilters method');
				} else {
					pageContextItems = this.input.getPageContextItems(this.selectors);
				}
			}
			return pageContextItems;
		},

		_convertEdgeFilter: function(itemContext, pageContextItem, valueType, options) {
			let pageContextValues = pageContextItem.getNetValues && pageContextItem.getNetValues();
			if(pageContextItem.getIsModeledFilter()){
				this._updateForModeledFilterContextItem(pageContextItem);
			}else if (pageContextItem.isRange()) {
				if (pageContextItem.getFrom() || pageContextItem.getTo()) {
					this.output.updateRangeFilter(itemContext,
						//updateSpec:
						{
							action: this.action || 'replace',
							exclude: (pageContextItem.getInvert()),
							from: pageContextItem.getFrom(),
							to: pageContextItem.getTo(),
							//Any range filters in page context are pre-aggregate.
							//Visualization filters should be post (though range selections aren't yet supported).
							preOrPost: pageContextItem.getOrigin() === FILTER ? 'pre' : 'post'
						},
						options);
				}
			} else if (pageContextValues) {
				let updateSpec = {
					action: this.action || 'replace',
					exclude: (pageContextItem.getExcludeForSpec() || this.exclude)
				};
				if (!_.isEmpty(pageContextValues)) {
					updateSpec.values = pageContextValues;
				} else if (pageContextItem.getIsNamedSet()) {
					updateSpec.valueDataItem = pageContextItem.getItemId();
				}else if(pageContextItem.getIsModeledFilter()){
					itemContext = this._getItemContextForModeledFilter();
				}
				this.output.updateFilter(itemContext,
					updateSpec,
					/*type*/
					valueType,
					options);
			}
		},

		/*Create an itemContext for global filter that uses a modeled filter*/
		_updateForModeledFilterContextItem(itemContext) {
			const itemId = itemContext.getItemId();
			this.output.updateFilter(
				{
					id: itemId,
					columnId: itemId,
					filterType: FILTER
				});
		},

		//FILTER COLLECTION IMPLEMENTATIONS INPUT/OUTPUT ======================================

		_getFiltersInput: function(filters) {
			return {
				getInputType: function() {
					return 'filters';
				},
				getFilterValues: function(itemContext) {
					var filterKey = itemContext.filterBins ? filters.getCategoryFilterKey(itemContext.itemId, itemContext.uniqueId) : filters.getOrdinalFilterKey(itemContext.itemId, itemContext.aggregationType);
					var filterEntry = filters.getFilterEntry(filterKey);
					return (filterEntry) ? filterEntry.values.toArray() : [];
				},
				getExclude: function(itemContext) {
					var filterKey = itemContext.filterBins ? filters.getCategoryFilterKey(itemContext.itemId, itemContext.uniqueId) : filters.getOrdinalFilterKey(itemContext.itemId, itemContext.aggregationType);
					var filterEntry = filters.getFilterEntry(filterKey);
					return (filterEntry &&
						filterEntry.operator &&
						filterEntry.operator.indexOf('not') >= 0) ? true : false;
				},
				getConditionsInfo: function(itemContext) {
					var filterEntry = filters.getFilterEntry({
						id: itemContext.filterBins ? itemContext.uniqueId : itemContext.itemId,
						columnId: itemContext.itemId
					});
					var conditionsInfo = {};
					if (filterEntry && filterEntry.conditions) {
						conditionsInfo.conditions = filterEntry.conditions;
						conditionsInfo.excludedValues = filterEntry.excludedValues ? filterEntry.excludedValues.toArray() : [];
					}
					return conditionsInfo;
				},
				getFilterOperator: function(itemContext) {
					var filterEntry = filters.getFilterEntry({
						id: itemContext.filterBins ? itemContext.uniqueId :itemContext.itemId,
						columnId: itemContext.itemId
					});
					return (filterEntry) ? filterEntry.operator : '';
				}
			};
		},

		/**
		 * If filter origin is visualization, the compound filter logic of multi selected data points should be OR, while all the other filters,
		 * such as tab filters and local filters should be AND. Need to propagate the visualizationId to FilterEntry to correctly differentiate
		 * AND / OR logic with different filter combinations.
		 */
		_getFiltersOutput: function(filters) {
			return {
				getOutputType: function() {
					return 'filters';
				},
				addDataPoints: function(itemContext, updateSpec, options) {
					let filterDefinition = {
						values: updateSpec.values,
						exclude: updateSpec.exclude,
						action: updateSpec.action,
						type: updateSpec.type
					};
					if (itemContext.origin === VISUALIZATION && itemContext.visualizationId) {
						filterDefinition.visualizationId = itemContext.visualizationId;
					}
					options.filterEntry = filters.addDataPointFilter(
						itemContext.itemIds,
						// TODO should stop passing binningAPIs when working on filters model class.
						itemContext.binningAPIs || itemContext.binning,
						filterDefinition,
						options
					);
					return filters.allFilterModificationComplete(options);
				},
				_updateItemContextWithBinningInfo(itemContext) {
					if (itemContext.binning) {
						itemContext.filterBins = true;
						// TODO should stop passing binningAPIs when working on filters model class.
						let binning = null;
						if (itemContext.binning.binningAPI) {
							binning = itemContext.binning.binningAPI;
							itemContext.binsLabel = binning.label;
							itemContext.uniqueId = itemContext.binning.id;
						} else {
							binning = Array.isArray(itemContext.binning) && itemContext.binning[0];
							itemContext.binsLabel = binning && binning.label;
							itemContext.uniqueId = binning && binning.dataItemId;
						}
					}
				},
				updateFilter: function(itemContext, updateSpec, type, options = {}) {
					if (itemContext.filterType === 'filter') {
						// model filter case
						options.filterEntry = filters.updateFilterEntry(itemContext);
					} else {
						type = type || updateSpec.type || null;
						options = options || {};
						this._updateItemContextWithBinningInfo(itemContext);
						var filterValues = updateSpec.values ? _.values(updateSpec.values) : null;
						if (filterValues && filterValues.length === 0 && !updateSpec.conditionsInfo && !updateSpec.exclude) {
							filters.removeFilterEntry({
								id: itemContext.filterBins ? itemContext.uniqueId : itemContext.itemId,
								columnId: itemContext.itemId
							});
						} else {
							var key = itemContext.filterBins ? filters.getCategoryFilterKey(itemContext.itemId, itemContext.uniqueId) : filters.getCategoryFilterKey(itemContext.itemId);
							options.filterEntry = filters.updateFilterEntry(
								//filters.getCategoryFilterKey(itemContext.itemId, itemContext.binsUniqueId), { //filterDefinition (filter-specific updateSpec)
								key, {
									type: type,
									values: filterValues,
									valueDataItem: updateSpec.valueDataItem,
									overrideHierarchyDefaultSelection: updateSpec.overrideHierarchyDefaultSelection,
									operator: (updateSpec.exclude ? 'notin' : 'in'),
									conditionsInfo: updateSpec.conditionsInfo,
									action: updateSpec.action || 'replace',
									filterBins: itemContext.filterBins,
									binsLabel: itemContext.itemName,
									id: itemContext.uniqueId,
									visualizationId: itemContext.visualizationId,
									isMissing: updateSpec.isMissing
								});
						}
					}
					return filters.allFilterModificationComplete(options);
				},
				updateRangeFilter: function(itemContext, updateSpec, options) {
					var operator = updateSpec.exclude ? 'notbetween' : 'between';
					var values = [];
					if (updateSpec.to === '') {
						operator = 'gt';
						values.push(updateSpec.from);
					} else if (updateSpec.from === '') {
						operator = 'lt';
						values.push(updateSpec.to);
					} else {
						values = [updateSpec.from, updateSpec.to];
					}
					const aggregationType = itemContext.aggregationType ? itemContext.aggregationType : updateSpec.aggregationType;
					options.filterEntry = filters.updateFilterEntry(
						filters.getOrdinalFilterKey(itemContext.itemId, aggregationType, updateSpec.preOrPost), { //Definition
							values: values,
							operator: operator,
							type: updateSpec.type,
							isMissing: updateSpec.isMissing
						},
						/*options*/
						options);
					return filters.allFilterModificationComplete(options);
				},
				removeFilterValues: function(itemContext, options) {
					var filterKey = itemContext.filterBins ? filters.getCategoryFilterKey(itemContext.itemId, itemContext.uniqueId) : filters.getOrdinalFilterKey(itemContext.itemId, itemContext.aggregationType);
					filters.removeFilterEntry(filterKey);
					return filters.allFilterModificationComplete(options);
				}
			};
		},

		//PAGE CONTEXT IMPLEMENTATIONS INPUT/OUTPUT ======================================

		_getPageContextInput: function(pageContext) {
			//For now, just return the pageContext when its an input source.
			return pageContext;
		},

		_getPageContextOutput: function(pageContext) {
			//This interface object is used to create a consistent output interface for a pageContext
			//target that is the same as the output interface for filters (or dataItems).
			//This allows easy transfer of the various filter types.
			//ie: for keep/Exclude, our input is the main pageContext (or a temporary pageContext) and output is a local filters collection
			//    for filters, our input is a temporary pageContext and the output is the main pageContext object.
			return {
				getOutputType: function() {
					return 'pagecontext';
				},
				addDataPoints: function(itemContext, updateSpec, options) {
					return pageContext.updateFilterContext(
						this._getFinalItemContext(itemContext),
						//updateSpec
						{
							command: 'update',
							exclude: updateSpec.exclude,
							values: updateSpec.values
						},
						this._getPageContextOptions(options)
					);
				},
				updateFilter: function(itemContext, updateSpec, options) {
					if (!updateSpec.values || updateSpec.values.length === 0) {
						return pageContext.deletePageContextItems(this._getFinalItemContext(itemContext), this._getPageContextOptions(options));
					} else {
						return pageContext.updateFilterContext(
							this._getFinalItemContext(itemContext),
							//updateSpec
							{
								command: 'update',
								exclude: (updateSpec.exclude) ? true : false,
								values: updateSpec.values,
								conditionsInfo: updateSpec.conditionsInfo
							},
							this._getPageContextOptions(options)
						);
					}
				},
				updateRangeFilter: function(itemContext, command, options) {
					return pageContext.updateFilterContext(
						this._getFinalItemContext(itemContext), {
							command: 'update',
							exclude: (command.exclude) ? true : false,
							values: [command.from, command.to]
						},
						this._getPageContextOptions(options)
					);
				},
				removeFilterValues: function(itemContext, options) {
					return pageContext.deletePageContextItems(this._getFinalItemContext(itemContext), this._getPageContextOptions(options));
				},

				/**
				 * @param itemContext - the initial itemContext containing either itemId or itemIds
				 * @returns an augmented itemContext with itemId/itemIds as 'hiearchies'
				 */
				_getFinalItemContext: function(itemContext) {
					if (!itemContext.itemId && !itemContext.itemIds) {
						return itemContext;
					}
					var itemIds = itemContext.itemIds || [itemContext.itemId];
					itemIds = Array.isArray(itemIds) ? itemIds : [itemIds];

					var hierarchies = _.map(itemIds, function(itemId) {
						return {
							hierarchyUniqueName: itemId
						};
					});
					var finalItemContext = _.clone(itemContext);
					finalItemContext.hierarchies = hierarchies;
					finalItemContext.hierarchyUniqueNames = [itemIds[0]]; //TODO: We shouldn't need this but pageContext complains.
					return finalItemContext;
				},

				//TODO: Some pageContext API's expect transactionId at the root of options.
				//		These api's really should allow transactionId to be either at the root or in the usual place (under payloadData/undoRedoTransactionId)
				_getPageContextOptions: function(options) {
					var pageContextOptions = _.clone(options);
					if (options) {
						pageContextOptions.transactionId = (options.transactionId || (options.payloadData && options.payloadData.undoRedoTransactionId));
					}
					return pageContextOptions;
				}
			};
		},

		//DATA ITEM SELECTION IMPLEMENTATIONS INPUT/OUTPUT ===============================
		_getDataItemInput: function( /*dataItemAPI*/ ) {
			return {
				getInputType: function() {
					return 'dataItem';
				}
			};
		},
		_getDataItemOutput: function( /*dataItemAPI*/ ) {
			return {
				getOutputType: function() {
					return 'dataItem';
				}
			};
		},

		destroy: function() {
			VisFilterProxy.inherited('destroy', this, arguments);
		}
	});

	return VisFilterProxy;
});
